import React, { useCallback, useContext } from 'react'
import type { CitationFormatType, SourceType } from '@grammarly/citation-generator'
import {
  CitationGeneratorProvider,
  CitationGeneratorPreview,
  CitationGeneratorForm,
  GetGrammarlyCtaCard,
} from '@grammarly/citation-generator'
import {
  TypeSectionCitationGenerator,
  TypeSectionCitationGeneratorFields,
} from '../../../lib/types/section-citation-generator'
import { KeywordSourceTypeRelationship } from '../../../lib/types/citation-keyword-metadata'
import { gnarClient, EventProps } from '../../../lib/tracking/gnar'
import { useCitationProgrammaticPageContext } from '../../../contexts/citation-programmatic-page-context'
import { PageContext } from '../../../contexts/page-context'
import { PageSection } from '../../page-section'
import { ContentProps, ContentRenderer } from '../../renderer'
import { useBrowserService, useUserService } from '../../../lib/hooks'
import { SectionGroup } from '../group'
import { GetGrammarlyCtaCardContent, GetGrammarlyModalContent } from './signup-wall'

import '@grammarly/citation-generator/dist/css/index.css'
import styles from './citation-generator.module.scss'

const defaultRootElementSelector = '#__next'

interface CitationGeneratorSectionContentProps {
  initialCitationFormat?: CitationFormatType
  initialCitationSource?: SourceType
}

const CitationGeneratorSectionContent: React.FC<CitationGeneratorSectionContentProps> = ({
  initialCitationFormat,
  initialCitationSource,
}) => {
  const { isMobile } = useBrowserService()
  const { pageId, slug } = useContext(PageContext)
  const gnarTrackFn = useCallback(
    (trackEventProps: EventProps) => gnarClient.track({ ...trackEventProps, pageId, pageSlug: slug }),
    [pageId, slug]
  )
  const { isAnonymous } = useUserService().currentUser
  const showSignUpWallCitations = !isMobile && isAnonymous

  return (
    <CitationGeneratorProvider
      clientName="cms"
      gnarTrackFn={gnarTrackFn}
      appElementForModal={defaultRootElementSelector}
      initialCitationFormat={initialCitationFormat}
      initialCitationSource={initialCitationSource}
      isAnonymous={isAnonymous}
    >
      <div className={styles.citationGeneratorContainer}>
        <div className={styles.citationGeneratorLeftColumn}>
          <CitationGeneratorForm />
        </div>
        <div className={styles.citationGeneratorRightColumn}>
          <CitationGeneratorPreview />
          {showSignUpWallCitations && (
            <GetGrammarlyCtaCard
              cardContent={<GetGrammarlyCtaCardContent pageId={pageId} slug={slug} />}
              modalContainerId={defaultRootElementSelector}
              copyCitationModalContent={
                <GetGrammarlyModalContent
                  modalTitle="Finish Your Citations Faster"
                  modalBody="Use Grammarly's browser extension to automatically create fully formatted citations from online sources as you research."
                  gnarCtaClickEventName="CitationGenerator_CopyCitationClickModal_SignUp"
                  pageId={pageId}
                  slug={slug}
                />
              }
              pageShowModalContent={
                <GetGrammarlyModalContent
                  modalTitle="Don't Miss Out on Auto-Citations"
                  modalBody="Make things easy on yourself. Use Grammarly's browser extension to get automatically generated citations from online sources as you research."
                  gnarCtaClickEventName="CitationGenerator_SecondPageShowModal_SignUp"
                  pageId={pageId}
                  slug={slug}
                />
              }
            />
          )}
        </div>
      </div>
    </CitationGeneratorProvider>
  )
}

export const CitationGeneratorSection: React.FC<ContentProps<TypeSectionCitationGenerator>> = ({ structure }) => {
  const {
    sys,
    fields: { internalName, background, desktopContentBelow, mobileFallback, defaultCitationFormat },
  } = structure
  const { isMobile } = useBrowserService()
  const { styleGuide, sourceType, sourceTypeRelationship } = useCitationProgrammaticPageContext()

  if (isMobile && mobileFallback) {
    return <ContentRenderer structure={mobileFallback} />
  }

  return (
    <>
      <PageSection
        internalName={internalName}
        sysStructure={sys}
        background={background}
        heading={getHeadingBySourceTypeRelationship(structure.fields, sourceTypeRelationship)}
      >
        <CitationGeneratorSectionContent
          initialCitationFormat={defaultCitationFormat || styleGuide}
          initialCitationSource={sourceType}
        />
      </PageSection>
      {desktopContentBelow && <SectionGroup className={styles.removeTopPadding} structure={desktopContentBelow} />}
    </>
  )
}

// #ToDo refactor to avoid fallthrough
/* eslint-disable no-fallthrough */
const getHeadingBySourceTypeRelationship = (
  fields: TypeSectionCitationGeneratorFields,
  sourceTypeRelationship?: KeywordSourceTypeRelationship
) => {
  switch (sourceTypeRelationship) {
    case KeywordSourceTypeRelationship.Equal:
      if (fields.headingProgrammaticEqual) {
        return fields.headingProgrammaticEqual
      }
    case KeywordSourceTypeRelationship.Mapped:
      if (fields.headingProgrammaticMapped) {
        return fields.headingProgrammaticMapped
      }
    case KeywordSourceTypeRelationship.MostCommonly:
      if (fields.headingProgrammaticMostCommonly) {
        return fields.headingProgrammaticMostCommonly
      }
    default:
      return fields.heading
  }
}
/* eslint-enable no-fallthrough */
